import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faUsers,
    faShieldAlt,
    faSearch,
    faTimes,
    faChevronDown,
    faChevronUp,
    faSort,
    faSortAmountDown,
    faSortAmountUp,
    faUniversity,
    faEnvelope,
    faCalendarAlt,
    faUserCircle,
    faCheckCircle,
    faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../hooks/useAuth';
import { isAdmin } from '../utils/adminConfig';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { ref, get } from 'firebase/database';
import { database } from '../firebase/firebase';
import { BankInfo } from '../types';

const ITEMS_PER_PAGE = 15;

interface UserSummary {
    uid: string;
    displayName: string | null;
    email: string | null;
    createdAt: number;
    bankInfo: BankInfo | null;
    eventSelectionsCount: number;
}

type SortField = 'createdAt' | 'displayName' | 'email';
type SortDir = 'asc' | 'desc';

const UserManagementPage: React.FC = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [users, setUsers] = useState<UserSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState<SortField>('createdAt');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [expandedUser, setExpandedUser] = useState<string | null>(null);
    // Store full user data for expanded view
    const [expandedUserData, setExpandedUserData] = useState<Record<string, any>>({});

    useEffect(() => {
        if (user && isAdmin(user.email)) {
            loadUsers();
        }
    }, [user]);

    const loadUsers = async () => {
        setLoading(true);
        try {
            // Step 1: Fetch userList (has parent-level .read: true)
            const userListRef = ref(database, 'userList');
            const listSnapshot = await get(userListRef);

            if (!listSnapshot.exists()) {
                setUsers([]);
                return;
            }

            const userIds = Object.keys(listSnapshot.val());

            // Step 2: Batch-fetch individual user profiles
            const BATCH_SIZE = 10;
            const usersList: UserSummary[] = [];

            for (let i = 0; i < userIds.length; i += BATCH_SIZE) {
                const batch = userIds.slice(i, i + BATCH_SIZE);
                const batchResults = await Promise.all(
                    batch.map(async (uid) => {
                        try {
                            const userRef = ref(database, `users/${uid}`);
                            const snapshot = await get(userRef);
                            if (!snapshot.exists()) return null;

                            const data = snapshot.val();
                            return {
                                uid,
                                displayName: data.displayName || null,
                                email: data.email || null,
                                createdAt: data.createdAt || 0,
                                bankInfo: data.bankInfo || null,
                                eventSelectionsCount: data.eventSelections
                                    ? Object.keys(data.eventSelections).length
                                    : 0,
                            } as UserSummary;
                        } catch {
                            return null;
                        }
                    })
                );
                usersList.push(...batchResults.filter((u): u is UserSummary => u !== null));
            }

            setUsers(usersList);
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadExpandedUserData = async (uid: string) => {
        if (expandedUserData[uid]) return; // Already loaded
        try {
            const userRef = ref(database, `users/${uid}`);
            const snapshot = await get(userRef);
            if (snapshot.exists()) {
                setExpandedUserData(prev => ({ ...prev, [uid]: snapshot.val() }));
            }
        } catch (error) {
            console.error('Error loading user details:', error);
        }
    };

    const handleToggleExpand = (uid: string) => {
        const isExpanding = expandedUser !== uid;
        setExpandedUser(prev => prev === uid ? null : uid);
        if (isExpanding) {
            loadExpandedUserData(uid);
        }
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('desc');
        }
    };

    // Filter & sort
    const filteredUsers = useMemo(() => {
        let result = users;

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(u =>
                (u.displayName || '').toLowerCase().includes(term) ||
                (u.email || '').toLowerCase().includes(term) ||
                u.uid.toLowerCase().includes(term)
            );
        }

        result = [...result].sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case 'displayName':
                    cmp = (a.displayName || '').localeCompare(b.displayName || '');
                    break;
                case 'email':
                    cmp = (a.email || '').localeCompare(b.email || '');
                    break;
                case 'createdAt':
                default:
                    cmp = a.createdAt - b.createdAt;
                    break;
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });

        return result;
    }, [users, searchTerm, sortField, sortDir]);

    // Pagination
    const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Stats
    const stats = useMemo(() => {
        const now = Date.now();
        const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
        return {
            totalUsers: users.length,
            withBankInfo: users.filter(u => u.bankInfo !== null).length,
            recentSignups: users.filter(u => u.createdAt > sevenDaysAgo).length,
            totalEventSelections: users.reduce((sum, u) => sum + u.eventSelectionsCount, 0),
        };
    }, [users]);

    const formatDate = (timestamp: number) => {
        if (!timestamp) return '—';
        return new Date(timestamp).toLocaleDateString('vi-VN', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    // Auth guards
    if (!user) {
        return (
            <div className="glass-page-bg flex items-center justify-center">
                <div className="glass-card p-8 text-center max-w-md">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-4xl text-yellow-500 mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {t('admin.users.auth.signInRequired')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {t('admin.users.auth.signInMessage')}
                    </p>
                    <Link to="/" className="glass-btn-primary">
                        {t('admin.users.auth.goHome')}
                    </Link>
                </div>
            </div>
        );
    }

    if (!isAdmin(user.email)) {
        return (
            <div className="glass-page-bg flex items-center justify-center">
                <div className="glass-card p-8 text-center max-w-md">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-4xl text-red-500 mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {t('admin.users.auth.accessDenied')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {t('admin.users.auth.adminRequired')}
                    </p>
                    <Link to="/" className="glass-btn-primary">
                        {t('admin.users.auth.goHome')}
                    </Link>
                </div>
            </div>
        );
    }

    if (loading) {
        return <LoadingSpinner />;
    }

    const sortOptions: { field: SortField; label: string }[] = [
        { field: 'createdAt', label: t('admin.users.sort.joinDate') },
        { field: 'displayName', label: t('admin.users.sort.name') },
        { field: 'email', label: t('admin.users.sort.email') },
    ];

    const SortButton = ({ field, label }: { field: SortField; label: string }) => (
        <button
            onClick={() => handleSort(field)}
            className={`flex items-center gap-1 text-xs font-medium transition-colors ${
                sortField === field
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
        >
            {label}
            <FontAwesomeIcon
                icon={sortField === field ? (sortDir === 'asc' ? faSortAmountUp : faSortAmountDown) : faSort}
                className="text-[10px]"
            />
        </button>
    );

    return (
        <div className="glass-page-bg">
            <div className="max-w-6xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="glass-card p-4 mb-6">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <Link to="/" className="glass-header-back">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {t('admin.users.header.title')}
                                </h1>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                                        {t('admin.users.header.adminBadge')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={loadUsers}
                            className="glass-btn-primary !px-3 !py-1.5 text-sm"
                        >
                            {t('admin.users.header.refresh')}
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalUsers}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.users.stats.totalUsers')}</p>
                    </div>
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.withBankInfo}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.users.stats.withBankInfo')}</p>
                    </div>
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.recentSignups}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.users.stats.recentSignups')}</p>
                    </div>
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.totalEventSelections}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.users.stats.totalEvents')}</p>
                    </div>
                </div>

                {/* Search & Sort */}
                <div className="glass-card p-4 mb-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                placeholder={t('admin.users.filters.searchPlaceholder')}
                                className="w-full pl-9 pr-8 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            {sortOptions.map(opt => (
                                <SortButton key={opt.field} field={opt.field} label={opt.label} />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Users List */}
                <div className="space-y-2">
                    {paginatedUsers.length === 0 ? (
                        <div className="glass-card p-8 text-center">
                            <FontAwesomeIcon icon={faUsers} className="text-3xl text-gray-400 mb-3" />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                                {t('admin.users.emptyState.title')}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {searchTerm
                                    ? t('admin.users.emptyState.noMatches')
                                    : t('admin.users.emptyState.noUsers')}
                            </p>
                        </div>
                    ) : (
                        paginatedUsers.map(u => (
                            <div key={u.uid} className="glass-card overflow-hidden">
                                <div
                                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                                    onClick={() => handleToggleExpand(u.uid)}
                                >
                                    {/* Avatar */}
                                    <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                                        <FontAwesomeIcon
                                            icon={faUserCircle}
                                            className="text-sm text-indigo-600 dark:text-indigo-400"
                                        />
                                    </div>

                                    {/* User info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                                            {u.displayName || 'No Name'}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            <span className="flex items-center gap-1 truncate">
                                                <FontAwesomeIcon icon={faEnvelope} className="text-[10px]" />
                                                {u.email || '—'}
                                            </span>
                                            <span className="hidden sm:inline flex-shrink-0">
                                                {formatDate(u.createdAt)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Bank info status */}
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                                            u.bankInfo
                                                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                                        }`}>
                                            <FontAwesomeIcon
                                                icon={u.bankInfo ? faCheckCircle : faTimesCircle}
                                                className="text-[10px]"
                                            />
                                            <span className="hidden sm:inline">
                                                <FontAwesomeIcon icon={faUniversity} className="text-[10px]" />
                                            </span>
                                        </span>
                                    </div>

                                    {/* Chevron */}
                                    <FontAwesomeIcon
                                        icon={expandedUser === u.uid ? faChevronUp : faChevronDown}
                                        className="text-xs text-gray-400 flex-shrink-0"
                                    />
                                </div>

                                {/* Expanded details */}
                                {expandedUser === u.uid && (
                                    <div className="px-4 pb-3 pt-1 border-t border-gray-200 dark:border-gray-700 animate-slide-down">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                            {/* UID */}
                                            <div className="glass-sub-panel rounded-lg p-3">
                                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                                    {t('admin.users.detail.uid')}
                                                </div>
                                                <div className="text-xs font-mono text-gray-900 dark:text-white break-all">
                                                    {u.uid}
                                                </div>
                                            </div>
                                            {/* Join date */}
                                            <div className="glass-sub-panel rounded-lg p-3">
                                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                                    {t('admin.users.detail.joinDate')}
                                                </div>
                                                <div className="text-xs text-gray-900 dark:text-white flex items-center gap-1.5">
                                                    <FontAwesomeIcon icon={faCalendarAlt} className="text-[10px] text-gray-400" />
                                                    {formatDate(u.createdAt)}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="glass-sub-panel rounded-lg p-3 mb-3">
                                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                                {t('admin.users.detail.email')}
                                            </div>
                                            <div className="text-xs text-gray-900 dark:text-white flex items-center gap-1.5">
                                                <FontAwesomeIcon icon={faEnvelope} className="text-[10px] text-gray-400" />
                                                {u.email || '—'}
                                            </div>
                                        </div>

                                        {/* Bank Info */}
                                        <div className="glass-sub-panel rounded-lg p-3 mb-3">
                                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                                {t('admin.users.detail.bankInfo')}
                                            </div>
                                            {u.bankInfo ? (
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                                                    <div>
                                                        <span className="text-gray-500 dark:text-gray-400">{t('admin.users.detail.bankDetails.bankName')}: </span>
                                                        <span className="font-medium text-gray-900 dark:text-white">{u.bankInfo.bankName}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500 dark:text-gray-400">{t('admin.users.detail.bankDetails.accountNumber')}: </span>
                                                        <span className="font-mono font-medium text-gray-900 dark:text-white">{u.bankInfo.accountNumber}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500 dark:text-gray-400">{t('admin.users.detail.bankDetails.accountHolder')}: </span>
                                                        <span className="font-medium text-gray-900 dark:text-white">{u.bankInfo.accountHolder}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                                                    {t('admin.users.detail.noBankInfo')}
                                                </p>
                                            )}
                                        </div>

                                        {/* Event Selections */}
                                        {expandedUserData[u.uid]?.eventSelections && (
                                            <div className="glass-sub-panel rounded-lg p-3">
                                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                                    {t('admin.users.detail.eventSelections')} ({Object.keys(expandedUserData[u.uid].eventSelections).length})
                                                </div>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {Object.entries(expandedUserData[u.uid].eventSelections).map(([eventId, selection]) => (
                                                        <Link
                                                            key={eventId}
                                                            to={`/event/${eventId}`}
                                                            className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            {eventId.slice(0, 12)}… → {String(selection).slice(0, 12)}
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {!expandedUserData[u.uid]?.eventSelections && u.eventSelectionsCount === 0 && (
                                            <div className="glass-sub-panel rounded-lg p-3">
                                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                                    {t('admin.users.detail.eventSelections')}
                                                </div>
                                                <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                                                    {t('admin.users.detail.noEventSelections')}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="glass-card p-3 mt-4 flex items-center justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t('admin.users.pagination.showing', {
                                start: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                                end: Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length),
                                total: filteredUsers.length
                            })}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                            >
                                ←
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                .map((page, i, arr) => (
                                    <React.Fragment key={page}>
                                        {i > 0 && arr[i - 1] !== page - 1 && (
                                            <span className="text-xs text-gray-400">…</span>
                                        )}
                                        <button
                                            onClick={() => setCurrentPage(page)}
                                            className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                                                currentPage === page
                                                    ? 'bg-indigo-500 text-white'
                                                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    </React.Fragment>
                                ))}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                            >
                                →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserManagementPage;
